// Store de sesión (Fase B). En modo demo el usuario fijo está siempre listo;
// con backend real se hidrata desde Supabase (usuario + perfil con rol) y
// escucha cambios de sesión. El rol decide la experiencia (súper admin /
// firma / cliente); si el perfil no carga, se asume 'client' (menor privilegio).
import { create } from 'zustand';
import type { Role } from '@/types/domain';
import { authService, emailOtpService, type SessionUser } from '@/services/auth';

interface SessionState {
  user: SessionUser | null;
  role: Role;
  orgName: string;
  /** 'loading' hasta hidratar la sesión inicial (evita parpadeo del guard). */
  status: 'loading' | 'ready';
  setUser: (user: SessionUser | null) => void;
  setProfile: (role: Role, orgName: string) => void;
}

const VALID_ROLES: Role[] = ['super_admin', 'admin', 'tax_preparer', 'notary', 'client', 'support', 'auditor'];

export const useSession = create<SessionState>((set) => ({
  user: null,
  role: 'client',
  orgName: '',
  status: 'loading',
  setUser: (user) => set({ user, status: 'ready' }),
  setProfile: (role, orgName) => set({ role, orgName }),
}));

async function hydrateProfile(user: SessionUser | null): Promise<void> {
  const s = useSession.getState();
  if (!user) {
    s.setProfile('client', '');
    return;
  }
  if (!authService.isReal) {
    // Modo demo: rol conmutable para previsualizar experiencias (solo demo).
    const demo = localStorage.getItem('vaultex.demoRole');
    s.setProfile(VALID_ROLES.includes(demo as Role) ? (demo as Role) : 'admin', 'Cassimmit Tax Group');
    return;
  }
  const meta = await authService.getProfileMeta(user.id);
  s.setProfile(
    meta && VALID_ROLES.includes(meta.role as Role) ? (meta.role as Role) : 'client',
    meta?.orgName ?? '',
  );
}

let started = false;
/** Hidrata la sesión una sola vez y deja el listener activo (idempotente). */
export function startSession(): void {
  if (started) return;
  started = true;
  void authService.getUser().then(async (u) => {
    // 0077: una sesión de contraseña que no pasó el código por correo no ve
    // datos; en vez de pantallas vacías se cierra y se dice por qué.
    if (u && authService.isReal) {
      const st = await emailOtpService.status();
      if (st?.required && !st.ok) {
        await authService.signOut({ silent: true });
        window.location.replace('/login?reason=otp');
        return;
      }
    }
    await hydrateProfile(u);
    useSession.getState().setUser(u);
  });
  authService.onAuthChange((u) => {
    void hydrateProfile(u).then(() => useSession.getState().setUser(u));
  });
}
